import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import AddPatientForm from './Patients/AddPatientForm';
import EditPatientForm from './Patients/EditPatientForm';
import DeleteConfirmationModal from './Patients/DeleteConfirmationModal';
import PatientList from './Patients/PatientList';
import PatientDetails from './Patients/PatientDetails';
import MedicalHistory from './Patients/MedicalHistory';

const Patients = () => {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [currentPatient, setCurrentPatient] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [patientHistory, setPatientHistory] = useState([]);
  const [showAddPatientForm, setShowAddPatientForm] = useState(false);
  const [showEditPatientForm, setShowEditPatientForm] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchPatients();
  }, []);

  const fetchPatients = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/api/users/patients');
      setPatients(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching patients:', err);
      setError('Failed to fetch patients data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const fetchPatientMedicalHistory = async (patientId) => {
    try {
      setLoading(true);
      const { data } = await api.get(`/api/appointments/patient/${patientId}/history`);
      return data;
    } catch (err) {
      console.error('Error fetching patient medical history:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const viewPatientDetails = (patient) => {
    setCurrentPatient(patient);
    setShowModal(true);
  };

  const viewPatientHistory = async (patient) => {
    try {
      setCurrentPatient(patient);
      const historyData = await fetchPatientMedicalHistory(patient._id);
      
      // Transform the data to match the expected format in the UI
      const formattedHistory = historyData.map(record => ({
        id: record._id,
        date: new Date(record.date).toISOString().split('T')[0],
        doctor: record.doctor ? record.doctor.name : 'Unknown Doctor',
        department: record.doctor && record.doctor.specialty ? record.doctor.specialty.name : 'General',
        diagnosis: record.diagnosis || 'Not specified',
        prescription: record.prescription || 'Not prescribed',
        notes: record.notes || 'No additional notes'
      }));
      
      setPatientHistory(formattedHistory);
      setShowHistoryModal(true);
    } catch (error) {
      console.error('Error loading medical history:', error);
      // If the API fails, we'll show a simplified version with a message
      setPatientHistory([]);
      setShowHistoryModal(true);
    }
  };

  const editPatient = (patient) => {
    setCurrentPatient(patient);
    setShowEditPatientForm(true);
  };

  const deletePatient = (patient) => {
    setCurrentPatient(patient);
    setShowDeleteConfirmation(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setActionLoading(true);
      await api.delete(`/api/users/patients/${currentPatient._id}`);
      setShowDeleteConfirmation(false);
      fetchPatients();
    } catch (err) {
      console.error('Error deleting patient:', err);
      alert('Failed to delete patient. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const closeAddPatientForm = () => {
    setShowAddPatientForm(false);
  };

  const closeEditPatientForm = () => {
    setShowEditPatientForm(false);
  };

  const closeDeleteConfirmation = () => {
    setShowDeleteConfirmation(false);
  };

  const handlePatientAdded = async () => {
    await fetchPatients();
    setShowAddPatientForm(false);
  };

  const handlePatientUpdated = async () => {
    await fetchPatients();
    setShowEditPatientForm(false);
  };

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error! </strong>
          <span className="block sm:inline">{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-800">Patients Management</h1>
        <p className="text-gray-600 mt-1">View and manage patient records</p>
      </div>
      
      {/* Search Bar and Add Patient Button */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center gap-4">
        <div className="relative flex-grow">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            type="text"
            placeholder="Search patients by name, email, phone, or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <button
          onClick={() => setShowAddPatientForm(true)}
          className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md flex items-center justify-center whitespace-nowrap"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Add Patient
        </button>
      </div>
      
      {/* Patient List Component */}
      <PatientList 
        patients={patients}
        searchTerm={searchTerm}
        onViewDetails={viewPatientDetails}
        onViewHistory={viewPatientHistory}
        onEditPatient={editPatient}
        onDeletePatient={deletePatient}
        loading={loading}
      />

      {/* Patient Details Modal */}
      {showModal && currentPatient && (
        <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-40 flex items-center justify-center">
          <div className="relative bg-white rounded-lg max-w-lg w-full mx-4 shadow-xl">
            <PatientDetails 
              patient={currentPatient} 
              onClose={() => setShowModal(false)} 
              onViewHistory={viewPatientHistory} 
            />
          </div>
        </div>
      )}

      {/* Patient Medical History Modal */}
      {showHistoryModal && currentPatient && (
        <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-40 flex items-center justify-center">
          <div className="relative bg-white rounded-lg max-w-3xl w-full mx-4 shadow-xl">
            <MedicalHistory 
              patient={currentPatient}
              medicalHistory={patientHistory}
              onClose={() => setShowHistoryModal(false)}
            />
          </div>
        </div>
      )}

      {/* Add Patient Form Modal */}
      {showAddPatientForm && (
        <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-40 flex items-center justify-center">
          <div className="relative bg-white rounded-lg w-full max-w-2xl mx-4 shadow-xl">
            <AddPatientForm onClose={closeAddPatientForm} onPatientAdded={handlePatientAdded} />
          </div>
        </div>
      )}

      {/* Edit Patient Form Modal */}
      {showEditPatientForm && currentPatient && (
        <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-40 flex items-center justify-center">
          <div className="relative bg-white rounded-lg w-full max-w-2xl mx-4 shadow-xl">
            <EditPatientForm 
              patient={currentPatient} 
              onClose={closeEditPatientForm} 
              onPatientUpdated={handlePatientUpdated} 
            />
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && currentPatient && (
        <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-40 flex items-center justify-center">
          <div className="relative bg-white rounded-lg w-full max-w-md mx-4 shadow-xl">
            <DeleteConfirmationModal 
              patient={currentPatient} 
              onClose={closeDeleteConfirmation} 
              onConfirm={handleDeleteConfirm}
              loading={actionLoading}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Patients;
